import { defineStore } from 'pinia'
import { ref, computed, readonly } from 'vue'
import LogtoClient from '@logto/client'

const config = () => ({
  endpoint: import.meta.env.VITE_LOGTO_ENDPOINT || 'https://auth.trama.lat',
  appId: import.meta.env.VITE_LOGTO_APP_ID || 'trama-spa-frontend',
  appSecret: import.meta.env.VITE_LOGTO_APP_SECRET || '',
  cookieEncryptionKey: import.meta.env.VITE_LOGTO_COOKIE_ENCRYPTION_KEY || '',
  sessionCookieKey: import.meta.env.VITE_LOGTO_SESSION_COOKIE_KEY || '',
  callbackUrl: import.meta.env.VITE_LOGTO_CALLBACK_URL || (typeof window !== 'undefined' ? `${window.location.origin}/callback` : ''),
  scopes: ['openid', 'profile', 'email', 'offline_access', 'roles', 'custom_data'],
  resources: [import.meta.env.VITE_LOGTO_API_RESOURCE || 'https://api.trama.lat'],
})

// localStorage-backed storage adapter for Logto
function createLocalStorageAdapter() {
  return {
    storage: {
      async getItem(key: string) {
        if (typeof window === 'undefined') return null
        return window.localStorage.getItem(key)
      },
      async setItem(key: string, value: string) {
        if (typeof window === 'undefined') return
        window.localStorage.setItem(key, value)
      },
      async removeItem(key: string) {
        if (typeof window === 'undefined') return
        window.localStorage.removeItem(key)
      },
    },
    async requester(input: RequestInfo | URL, init?: RequestInit) {
      const response = await fetch(input, init)
      if (!response.ok) {
        const errorBody = await response.json().catch(() => ({}))
        console.error(`[TRAMA-AUTH] Logto requester error: [status=${response.status}]`, errorBody)
        throw new Error(`Logto request failed: ${response.status}`)
      }
      return response.json()
    },
    unstable_cache: {
      async getItem(_key: string) {
        return null
      },
      async setItem(_key: string, _value: string) {
        // noop
      },
      async removeItem(_key: string) {
        // noop
      },
    },
    navigate: (url: string) => {
      if (typeof window !== 'undefined') {
        window.location.href = url
      }
    },
    generateState: () => {
      if (typeof window === 'undefined') return ''
      return (
        (window.crypto?.randomUUID?.() ??
          Math.random().toString(36).slice(2)) + Date.now().toString(36)
      )
    },
    generateCodeVerifier: () => {
      if (typeof window === 'undefined') return ''
      const arr = new Uint8Array(32)
      window.crypto?.getRandomValues(arr) ?? arr.fill(0)
      return btoa(String.fromCharCode(...arr))
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
    },
    generateCodeChallenge: async (codeVerifier: string) => {
      if (typeof window === 'undefined' || !window.crypto?.subtle) return codeVerifier
      const data = new TextEncoder().encode(codeVerifier)
      const hash = await window.crypto.subtle.digest('SHA-256', data)
      return btoa(String.fromCharCode(...new Uint8Array(hash)))
        .replace(/=/g, '')
        .replace(/\+/g, '-')
        .replace(/\//g, '_')
    },
  }
}

export const useAuthStore = defineStore('auth', () => {
  const user = ref<any>(null)
  const accessToken = ref<string | null>(null)
  const isLoading = ref(false)
  const isBootstrapped = ref(false)
  const error = ref<string | null>(null)
  let client: LogtoClient | null = null

  const isAuthenticated = computed(() => !!user.value)

  function getClient(): LogtoClient {
    if (typeof window === 'undefined') {
      throw new Error('LogtoClient can only be used in the browser')
    }
    if (!client) {
      client = new LogtoClient(config(), createLocalStorageAdapter() as any)
    }
    return client
  }

  function decodeJwtPayload(token: string): any {
    try {
      const parts = token.split('.')
      if (parts.length !== 3) return null
      const payload = parts[1].replace(/-/g, '+').replace(/_/g, '/')
      const padded = payload + '='.repeat((4 - (payload.length % 4)) % 4)
      return JSON.parse(atob(padded))
    } catch {
      return null
    }
  }

  function clearStaleLogtoStorage() {
    if (typeof window === 'undefined') return
    try {
      window.localStorage.removeItem('idToken')
      window.localStorage.removeItem('refreshToken')
      window.localStorage.removeItem('accessToken')
      window.localStorage.removeItem('signInSession')
    } catch {
      // ignore
    }
  }

  // Version key — bump to force all users to re-authenticate on next bootstrap
  // This is useful when rotating the Logto appId or making other auth-breaking changes
  const AUTH_VERSION_KEY = 'trama.authVersion'
  const CURRENT_AUTH_VERSION = '2' // Bumped when switching to trama-spa-frontend

  function checkAuthVersion() {
    if (typeof window === 'undefined') return
    const stored = window.localStorage.getItem(AUTH_VERSION_KEY)
    if (stored !== CURRENT_AUTH_VERSION) {
      console.warn(
        `[TRAMA-AUTH] Auth version mismatch (stored=${stored}, expected=${CURRENT_AUTH_VERSION}). Clearing all Logto storage.`,
      )
      clearStaleLogtoStorage()
      // Also clear the LogtoClient's in-memory accessTokenMap
      // (it will be reset when getClient() is called fresh, but clear all known keys)
      try {
        const keysToRemove: string[] = []
        for (let i = 0; i < window.localStorage.length; i++) {
          const k = window.localStorage.key(i)
          if (k && (k.startsWith('LogtoClient') || k.includes('logto'))) {
            keysToRemove.push(k)
          }
        }
        keysToRemove.forEach((k) => window.localStorage.removeItem(k))
      } catch {
        // ignore
      }
      window.localStorage.setItem(AUTH_VERSION_KEY, CURRENT_AUTH_VERSION)
    }
  }

  async function bootstrap() {
    if (isBootstrapped.value) return
    if (typeof window === 'undefined') return
    isLoading.value = true

    // Force re-auth on version mismatch (e.g. after appId rotation)
    checkAuthVersion()

    // Detect and clear stale tokens from a previous Logto app (e.g. after appId rotation)
    try {
      const currentAppId = config().appId
      const storedIdToken = window.localStorage.getItem('idToken')
      if (storedIdToken) {
        const claims = decodeJwtPayload(storedIdToken)
        const tokenClientId = claims?.client_id || claims?.aud
        if (tokenClientId && tokenClientId !== currentAppId) {
          console.warn(
            `[TRAMA-AUTH] Stale token detected (client_id=${tokenClientId}, expected=${currentAppId}). Clearing session.`,
          )
          clearStaleLogtoStorage()
        }
      }
    } catch {
      // ignore
    }

    // Always reset the in-memory LogtoClient to force a fresh instance with current config
    client = null

    try {
      const c = getClient()
      const isAuthed = await c.isAuthenticated()
      if (isAuthed) {
        const claims = await c.getIdTokenClaims()
        let userInfo: any = null
        try {
          userInfo = await c.fetchUserInfo()
        } catch (e) {
          console.warn('[auth] Failed to fetch user info:', e)
        }
        
        const token = (await c.getAccessToken(config().resources[0])) ?? null
        accessToken.value = token
        
        let localUser: any = null
        if (token) {
          try {
            const apiBase = import.meta.env.VITE_API_BASE_URL || 'https://api.trama.lat/api/v1'
            const res = await fetch(`${apiBase}/users/me`, {
              headers: { Authorization: `Bearer ${token}` }
            })
            if (res.ok) {
              localUser = await res.json()
            }
          } catch (e) {
            console.warn('[auth] Failed to fetch /users/me:', e)
          }
        }

        // Nombre mostrado en el saludo del dashboard ("¡Hola, X!") y en
        // el footer del sidebar. Orden de preferencia:
        //  1. `users.name` local (/users/me) — es el que el usuario edita
        //     en Configuración, así que es el más fresco.
        //  2. `name` del id_token / userinfo de Logto.
        //  3. La parte local del email, para no caer en el genérico
        //     "Usuario" cuando el perfil no tiene nombre cargado.
        const email = localUser?.email || claims.email || userInfo?.email || null
        const resolvedName =
          localUser?.name?.trim() ||
          (typeof claims.name === 'string' ? claims.name.trim() : '') ||
          (typeof userInfo?.name === 'string' ? userInfo.name.trim() : '') ||
          email?.split('@')[0] ||
          null

        user.value = {
          id: claims.sub,
          email,
          name: resolvedName,
          avatarUrl: claims.picture ?? userInfo?.picture ?? null,
          // companyId is NOT derived here anymore. The source of truth for
          // the active company is `settings.activeCompanyId` (backed by
          // user_companies membership). Kept on the type for backwards-compat
          // with any residual readers, but always null — migrate to the
          // settings store.
          companyId: null,
        }
        console.log('[auth] Bootstrap complete. user:', user.value)
      }
    } catch (e: any) {
      error.value = e?.message ?? 'No se pudo inicializar la sesión'
      client = null
    } finally {
      isLoading.value = false
      isBootstrapped.value = true
    }
  }

  async function signIn() {
    const c = getClient()
    await c.signIn(config().callbackUrl)
  }

  async function handleCallback(callbackUrl: string) {
    const c = getClient()
    await c.handleSignInCallback(callbackUrl)
    isBootstrapped.value = false
    await bootstrap()
  }

  function clearAllAuthState() {
    if (typeof window === 'undefined') return
    // Clear all known Logto localStorage keys
    clearStaleLogtoStorage()
    // Scan and clear any other Logto-related keys
    try {
      const keysToRemove: string[] = []
      for (let i = 0; i < window.localStorage.length; i++) {
        const k = window.localStorage.key(i)
        if (k && k !== AUTH_VERSION_KEY && (k.startsWith('LogtoClient') || k.includes('logto'))) {
          keysToRemove.push(k)
        }
      }
      keysToRemove.forEach((k) => window.localStorage.removeItem(k))
    } catch {
      // ignore
    }
    // Reset in-memory state
    user.value = null
    accessToken.value = null
    isBootstrapped.value = false
    client = null
  }

  async function signOut() {
    // Always clear local state first so the user is logged out even if the
    // Logto SDK call fails (e.g. network error, invalid refresh token, etc.)
    const postLogoutRedirectUri = typeof window !== 'undefined' ? window.location.origin : ''

    // Attempt the Logto SDK signOut (revokes refresh token at Logto, then
    // navigates to Logto's end-session endpoint). This may fail or navigate
    // away — that's fine, our local state is already cleared.
    try {
      const c = getClient()
      // Don't await navigation — it may redirect the page
      c.signOut(postLogoutRedirectUri).catch(() => {
        // ignore SDK errors; we still clear local state below
      })
    } catch {
      // ignore
    }

    // Immediately clear all local auth state
    clearAllAuthState()
  }

  async function getAccessToken(): Promise<string | null> {
    if (typeof window === 'undefined' || !client) return null
    try {
      return (await client.getAccessToken(config().resources[0])) ?? null
    } catch {
      return null
    }
  }

  return {
    user: readonly(user),
    accessToken: readonly(accessToken),
    isAuthenticated,
    isLoading: readonly(isLoading),
    isBootstrapped: readonly(isBootstrapped),
    error: readonly(error),
    bootstrap,
    signIn,
    handleCallback,
    signOut,
    getAccessToken,
  }
})
